import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export interface Tax {
  id: string;
  name: string;
  rate: string | number;
  type: 'percentage' | 'fixed';
  is_inclusive: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TaxRequest {
  name: string;
  rate: number;
  type: 'percentage' | 'fixed';
  is_inclusive?: boolean;
  is_active?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class TaxesService {
  private readonly apiUrl = `${environment.apiUrl}/taxes`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown consumers (e.g. menu-item form). */
  getAll(): Observable<ApiResponse<Tax[]>> {
    return this.http.get<ApiResponse<Tax[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the taxes list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<Tax>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<Tax[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<Tax>> {
    return this.http.get<ApiResponse<Tax>>(`${this.apiUrl}/${id}`);
  }

  create(data: TaxRequest): Observable<ApiResponse<Tax>> {
    return this.http.post<ApiResponse<Tax>>(this.apiUrl, data);
  }

  update(id: string, data: Partial<TaxRequest>): Observable<ApiResponse<Tax>> {
    return this.http.put<ApiResponse<Tax>>(`${this.apiUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
